import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Col, Input, Row, Label, ModalBody, Form, TabPane, TabContent, Spinner, FormFeedback, FormGroup, CardBody, Card, Table, Alert, } from 'reactstrap';

// Formik
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import moment from "moment";

//redux
import { values } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { DatePickerInput, DatesProvider, DateTimePicker } from '@mantine/dates';
import { setRequireCloseModal } from '../../../slices/personalProtectiveEquipment/reducer';
import { getPersonList, } from '../../../helpers/establishment/backend_helper';
import { deliverWarehouseItem, deliverWarehouseItemStockCheck, getEstablishmentTypeList, getPersonalProtectiveEquipmentList, getPersonalProtectiveEquipmentStatusByCode, getWarehouseGroupExist, updatePersonalProtectiveEquipment, uploadFoldersFile, } from '../../../slices/thunks';
import OSelect from '../../../Components/OElements/OSelect';
import SimpleBar from 'simplebar-react';
import useLogoLoader from '../../../Components/Hooks/useLogoLoaderHooks';
import useFileType from '../../../Components/Hooks/useFileTypeHooks';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { getWarehouseGroup } from '../../../helpers/warehouse/backend_helper';
import OInput from '../../../Components/OElements/OInput';

const PersonalProtectiveEquipmentDelivery = ({ dataPersonalProtectiveEquipment, handleToggleModalDeliver, isOpen, activeTab, toggleTab, }) => {
	const dispatch = useDispatch();
	const { logoSrc } = useLogoLoader('login');

	const [folderPaths, setFolderPaths] = useState([]);
	const [selectedEstablishment, setSelectedEstablishment] = useState(null);
	const [selectedPerson, setSelectedPerson] = useState(null);
	const [loading, setLoading] = useState(false);
	const [typeDocument, setTypeDocument] = useState(null);

	const [fileName, setFileName] = useState('');
	const { file_type } = useFileType('EPP');

	const selectorEstablishmentTypes = createSelector(
		state => state.EstablishmentTypes,
		data => ({
			itemEstablishmentTypeList: data.itemEstablishmentTypeList,
		})
	);

	const { itemEstablishmentTypeList, } = useSelector(selectorEstablishmentTypes);

	const selectorPersonalProtectiveEquipment = createSelector(
		state => state.PersonalProtectiveEquipments,
		data => ({
			isLoadingList: data.isLoadingList,
			requireCloseModal: data.requireCloseModal,
			itemPersonalProtectiveEquipmentDetail: data.itemPersonalProtectiveEquipmentDetail,
		})
	);

	const { isLoadingList, requireCloseModal, itemPersonalProtectiveEquipmentDetail } = useSelector(selectorPersonalProtectiveEquipment);

	const selectorWarehouse = createSelector(
		state => state.Warehouses,
		data => ({
			itemWarehouseDetail: data.itemWarehouseDetail,
		})
	);

	const { itemWarehouseDetail } = useSelector(selectorWarehouse);


	const selectorItems = createSelector(
		state => state.WarehouseItems,
		data => ({
			isCheckStock: data.isCheckStock,
			isCheckStockOk: data.isCheckStockOk,
			checkStockErrors: data.checkStockErrors
		})
	);

	const {
		isCheckStock,
		isCheckStockOk,
		checkStockErrors
	} = useSelector(selectorItems);

	useEffect(() => {
		if(isOpen && dataPersonalProtectiveEquipment?._id) {
			dispatch(deliverWarehouseItemStockCheck({personal_protective_equipment_ids: [dataPersonalProtectiveEquipment._id]}))
		}
	}, [isOpen, dataPersonalProtectiveEquipment])


	useEffect(() => {
		dispatch(getEstablishmentTypeList());
		dispatch(getPersonalProtectiveEquipmentStatusByCode('END'));
	}, []);

	const uploadDocument = async (file, folderPaths, fileTypeId, establishmentId) => {
		if (!(file instanceof File)) {
			throw new Error("El archivo seleccionado no es válido.");
		}

		const formData = new FormData();
		formData.append("file", file);
		formData.append("file_type_id", fileTypeId);
		formData.append("establishment_id", establishmentId);

		folderPaths.forEach(path => formData.append("folder_paths", path));

		try {
			const response = await dispatch(uploadFoldersFile(formData)).unwrap();
			return response;
		} catch (error) {
			console.error("Error uploading file:", error);
			throw error;
		}
	};


	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			delivery_person_id: '',
			delivery_date: '',
			document: '',
			file: '',
		},
		validationSchema: Yup.object({
			delivery_person_id: Yup.string().required('Campo requerido'),
			delivery_date: Yup.string().required('Campo requerido'),
			document: Yup.string().required('Campo requerido'),
			file: Yup.string().nullable(),
		}),
		onSubmit: async (values) => {
			setLoading(true);

			try {
				const folder = buildFolderPath(dataPersonalProtectiveEquipment, itemEstablishmentTypeList);
				const updatedFolderPaths = [...folderPaths, folder];

				const documents = await uploadDocument(
					values.document,
					updatedFolderPaths,
					file_type._id,
					dataPersonalProtectiveEquipment.establishment_id
				);
				const document = documents[documents.length - 1];
				
				await dispatch(
					deliverWarehouseItem({
						personal_protective_equipment_ids: [dataPersonalProtectiveEquipment._id],
						delivery_person_id: delivery_person_id,
						delivery_person_id: values.delivery_person_id,
						delivery_date: values.delivery_date,
						document_id: document._id
					})
				)

				dispatch(getPersonalProtectiveEquipmentList());
				dispatch(setRequireCloseModal(true));
				handleToggleModalDeliver();
			} catch (error) {
				console.error(error);
				toast.error('Ocurrió un error al procesar la entrega.');
			} finally {
				setLoading(false);
			}

		}
	});

	// Función auxiliar para construir el folder path
	function buildFolderPath(data, typeList) {
		const type = typeList.find(t => t._id === data.establishment.establishment_type_id);
		const typeName = type?.name || '';
		const codePart = data.establishment.code_siep ? `${data.establishment.code_siep} - ` : '';
		return `/${typeName}/${codePart}${data.establishment.name}/Abastecimiento/EPP/Entrega`;
	}

	// Función auxiliar para preparar la lista de transferencias
	async function buildTransferList(data) {
		const list = [];
		let hasError = false;

		for (const item of data.assigned_items) {
			try {
				const response = await dispatch(getWarehouseGroupExist({
					establishment_id: data.establishment_id,
					group_code: item.warehouse_group_code
				}));

				list.push({
					source_warehouse_code: item.warehouse_code,
					destination_warehouse_code: response.payload.group.warehouse_code,
					warehouse_item_id: item.warehouse_item_id,
					quantity: item.quantity
				});
			} catch (error) {
				hasError = true;
				console.error("Error al obtener grupo de almacén:", item, error);
			}
		}

		if (hasError) {
			toast.error('Hubo errores con algunos ítems. Verifique configuración de almacenes.');
			setLoading(false);
			return null;
		}

		return list;
	}

	// Función auxiliar para construir el formData final
	function buildFormData(values, documentId, data, assignedItems) {
		return {
			_id: data._id,
			assigned_items: assignedItems,
			person_id: data.person_id,
			establishment_id: data.establishment_id,
			warehouse_id: data.warehouse_id,
			delivery_person_id: values.delivery_person_id,
			delivery_date: values.delivery_date,
			pick_person_id: data.pick_person_id,
			pick_date: data.pick_date,
			document_id: documentId,
			assignment_date: data.assignment_date,
			personal_protective_equipment_status_id: itemPersonalProtectiveEquipmentDetail._id,
			personal_protective_equipment_reason_id: data.personal_protective_equipment_reason_id,
			cost_center: selectedPerson.cost_center?.code || null,
			management_id: selectedPerson.management?._id || null,
		};
	}






	const columns = useMemo(
		() => [
			{ header: 'Elemento', accessorKey: 'name', size: 'auto' },
			{ header: 'Talla', accessorKey: 'size', size: 'auto' },
			{ header: 'Cantidad', accessorKey: 'quantity', size: 'auto' },
			{ header: 'Unidad', accessorKey: 'unit_measurement_name', size: 'auto' },
		],
	);

	const fileInputRef = useRef();

	const handleFileClear = () => {
		fileInputRef.current.value = ''; // Resetea el input file manualmente
		setFileName('');
		validation.setFieldValue('document', '');
		validation.setFieldValue('file', '');
	};

	const handleFileChange = (event) => {
		const file = event.target.files[0] || null;
		if (file) {
			validation.setFieldValue("document", file);
			validation.setFieldValue("file", file);
			setFileName(file.name); // Solo actualizamos el nombre del archivo para mostrarlo en UI
		} else {
			validation.setFieldValue("document", '');
			validation.setFieldValue("file", '');
			setFileName('');
		}
	};

	const rawAssignmentDate = dataPersonalProtectiveEquipment?.assignment_date;
	const parsedAssignmentDate = moment(rawAssignmentDate);
	const minDate = parsedAssignmentDate.isValid()
		? parsedAssignmentDate.toDate()
		: new Date(new Date().setDate(new Date().getDate() - 10));
	
	return (
		<Form
			className='tablelist-form'
			onSubmit={e => {
				e.preventDefault();
				if (Object.keys(validation.errors).length != 0) { toast.error('Por favor, revisa la información ingresada. Hemos encontrado algunos detalles que necesitan corrección antes de continuar.', { autoClose: 4000 }); }
				validation.handleSubmit();
				return false;
			}}
		>
			
			<ModalBody className='modal-body'>
				<React.Fragment>
					{
						isCheckStock ? (
							<Alert color='primary'>
								<Spinner size='sm' className='me-2' />
								Estamos comprobando el stock de los items en bodega, por favor espere...
							</Alert>
						)
						: (
							!isCheckStockOk ? (
								<Alert color='danger'>
									<p className='mb-0'>
										<b>{checkStockErrors?.message}:</b>
									</p>

									{checkStockErrors?.errors?.length ? (
										<ul className='mt-2 mb-0'>
											{checkStockErrors.errors.map((item, index) => (
												<li key={index}>{item}</li>
											))}
										</ul>
									) : null}
								</Alert>
							) :
							null
						)
					}
				</React.Fragment>
				<TabContent activeTab={activeTab}>
					<TabPane tabId='1'>
						<Row className='g-3'>
							<Col lg={3}>
								<OSelect
									key={`delivery_person_id${values.delivery_person_id}` || ''}
									label='Persona que Entrega'
									id='delivery_person_id'
									name='delivery_person_id'
									isRequired={true}
									value={validation.values.delivery_person_id || ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									onSelect={setSelectedPerson}
									requestApi={() => getPersonList({})}
									validation={validation}
								/>
							</Col>
							<Col sm={3}>
								<DatesProvider settings={{ locale: 'es-mx', firstDayOfWeek: 1, weekendDays: [0], timezone: 'UTC' }}>
									<DatePickerInput
										label={
											<span>
												Fecha de Entrega <span style={{ color: 'red' }}>*</span>
											</span>
										}
										id='delivery_date'
										name='delivery_date'
										key={'delivery_date_' + (dataPersonalProtectiveEquipment?.assignment_date || 'sin_fecha')}
										placeholder="Seleccione una fecha"
										value={
											validation.values.delivery_date
												? new Date(validation.values.delivery_date)
												: null
										}
										onChange={(date) => { validation.setFieldValue("delivery_date", date); }}
										onBlur={validation.handleBlur}
										valueFormat='DD-MM-YYYY'
										maxDate={new Date()}
										// minDate={new Date(new Date().setDate(new Date().getDate() - 10))} // Set minDate to 5 days before today
										minDate={minDate}
										error={
											validation.touched['delivery_date'] && validation.errors['delivery_date']
												? validation.errors['delivery_date']
												: ''
										}
										clearable={true}
									/>
								</DatesProvider>
							</Col>

							<Col lg={6}>
								<Label for="document">Documento Firmado <span style={{ color: 'red' }}>*</span></Label>
								<Input
									ref={fileInputRef}
									name='document'
									id='document'
									className='form-control'
									placeholder='Ingrese el documento'
									type='file'
									validate={{ required: { value: true } }}
									// value={fileName ? undefined : ''} // Resetea el valor si no hay archivo
									onChange={handleFileChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.document && validation.errors.document ? true : false}
								/>
								{validation.touched.document &&
									validation.errors.document ? (
									<FormFeedback type='invalid'>
										{validation.errors.document}
									</FormFeedback>
								) : null}
							</Col>
						</Row>
					</TabPane>
					<TabPane tabId='2'>
						<Row>
							<Col lg={12}>
								<SimpleBar style={{ maxHeight: "300px" }}>
									<ODataGrid
										enableTopToolbar={false}
										columns={columns}
										data={dataPersonalProtectiveEquipment.assigned_items}
										nameFile={'items_para_entrega'}
									/>
								</SimpleBar>
							</Col>
						</Row>
					</TabPane>
				</TabContent>
			</ModalBody >
			
			<div className='modal-footer'>
				<div className='hstack gap-2 justify-content-end'>
					<Button
						type='button'
						onClick={() => {
							handleToggleModalDeliver();
						}}
						className='btn-danger btn-label'
					>
						<i className='icon-x label-icon'></i>
						Cancelar
					</Button>
					<button
						type='submit'
						className='btn btn-label btn-primary'
						id='add-btn'
						disabled={loading || isCheckStock || !isCheckStockOk}
					>
						{loading ? (<i className='label-icon'><Spinner size='sm' /></i>) : (<i className='ri-save-3-line label-icon'></i>)}
						Entregar Elementos de Protección Personal
					</button>
				</div>
			</div>
		</Form >
	);
};

export default PersonalProtectiveEquipmentDelivery;
